import React, { useEffect, useRef, useState } from 'react';
import { IErrorMessageOverrides } from "../../../models/configuration/plugin/IConfiguration";
import { Box, Button, Stack, Typography } from '@mui/material';
import { base64DecodeMessages, base64Encode } from '../../../utils/convert';
import { DtDataGrid } from '../../ui/dataGrid/DtDataGrid';
import { DataGridActionConfig, DTDataGridColumnType, IDataColumn } from '../../ui/dataGrid';
import { DtPageHeader } from '../../ui/header';
import RespMessageDialog from './RespMessageDialog';
import colorTheme from '../../../assets/colorTheme';
import { useSnackbar } from '../../../hooks/useSnackbar'; // <-- import the hook

export interface IRespMessagesProps {
    messages: IErrorMessageOverrides[];
    updateMessages: (updatedMessages: IErrorMessageOverrides[]) => void;
}

const RespMessages: React.FC<IRespMessagesProps> = ({ messages, updateMessages }) => {
    // State
    const [messagesState, setMessagesState] = useState<IErrorMessageOverrides[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"add" | "edit" | "view">("add");
    const [dialogInitial, setDialogInitial] = useState<{ ResponseMessage: string; ResponseOverride: string }>({ ResponseMessage: "", ResponseOverride: "" });
    const [editRowIndex, setEditRowIndex] = useState<number | null>(null);
    const dataGridRef = useRef<any>(null);

    // Snackbar hook
    const { showSnackbar, snackbar } = useSnackbar();

    // Decode messages on mount or when messages prop changes
    useEffect(() => {
        if (messages && messages.length > 0) {
            setMessagesState(base64DecodeMessages(messages));
        } else {
            setMessagesState([]);
        }
    }, [messages]);

    // Encode messages for parent
    const encodeMessages = (msgs: IErrorMessageOverrides[]) =>
        msgs.map(m => ({
            ResponseMessage: base64Encode(m.ResponseMessage),
            ResponseOverride: base64Encode(m.ResponseOverride)
        }));

    // DataGrid columns
    const columns: IDataColumn[] = [
    {
        name: 'Response Message',
        fieldId: 'ResponseMessage',
        datatype: 'text' as DTDataGridColumnType,
        required: true,
        editable: true,
        sortable: true,
        tooltip: (row) => row.ResponseMessage,
        sx: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "top",
        }
    },
    {
        name: 'Response Message Override',
        fieldId: 'ResponseOverride',
        datatype: 'text' as DTDataGridColumnType,
        required: true,
        editable: true,
        sortable: true,
        tooltip: (row) => row.ResponseOverride,
        sx: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            verticalAlign: "top",
        }
    }
];

    // DataGrid actions
    const actionsConfig: DataGridActionConfig = {
        mode: 'inline',
        actions: [
            {
                type: 'view',
                label: 'View',
                onClick: (row: any, rowIndex?: number) => {
                    setDialogMode("view");
                    setDialogInitial({
                        ResponseMessage: row.ResponseMessage,
                        ResponseOverride: row.ResponseOverride
                    });
                    setDialogOpen(true);
                }
            }
        ]
    };

    // add handler: open dialog
    const handleAdd = () => {
        setDialogMode("add");
        setDialogInitial({ ResponseMessage: "", ResponseOverride: "" });
        setEditRowIndex(null);
        setDialogOpen(true);
    };

    // edit handler: open dialog
    const handleEdit = (row: { ResponseMessage: string; ResponseOverride: string }, rowIndex: number) => {
        setDialogMode("edit");
        setDialogInitial(row);
        setEditRowIndex(rowIndex);
        setDialogOpen(true);
    };

    //  delete handler: remove item
    const handleDelete = (row: { ResponseMessage: string; ResponseOverride: string }) => {
        const updated = messagesState.filter((m) => m.ResponseMessage !== row.ResponseMessage);
        setMessagesState(updated);
        updateMessages(encodeMessages(updated));
        showSnackbar('Item deleted successfully.', 'success');
    };

    // Save from dialog
    const handleDialogSave = (values: { ResponseMessage: string; ResponseOverride: string }) => {
        if (!values.ResponseMessage) return;
        if (dialogMode === "add") {
            if (messagesState.find(m => m.ResponseMessage === values.ResponseMessage)) {
                showSnackbar("The Response Message already exists. Please enter a different one.", "error");
                return;
            }
            const updated = [...messagesState, values];
            setMessagesState(updated);
            updateMessages(encodeMessages(updated));
            showSnackbar('Item added successfully.', 'success');
            setTimeout(() => {
                dataGridRef.current?.moveToLastPage?.();
            }, 0);
        } else if (dialogMode === "edit" && editRowIndex !== null) {
            const updated = messagesState.map((m, idx) =>
                idx === editRowIndex ? values : m
            );
            setMessagesState(updated);
            updateMessages(encodeMessages(updated));
            showSnackbar('Item updated successfully.', 'success');
        }
        setDialogOpen(false);
    };

    // Cancel dialog
    const handleDialogCancel = () => setDialogOpen(false);

    return (
        <Stack direction='column' gap={0} >
            <DtPageHeader actions={<Button onClick={handleAdd} variant='contained' size='small'>Add Override</Button>}>Response Messages</DtPageHeader>

            <Box>
                <DtDataGrid
                    ref={dataGridRef}
                    columns={columns}
                    data={messagesState}
                    paged={true}
                    actionsConfig={actionsConfig}
                    viewConfig={{
                        label: 'View',
                        onView(row) {
                            setDialogMode("view");
                            setDialogInitial({
                                ResponseMessage: row.ResponseMessage,
                                ResponseOverride: row.ResponseOverride
                            });
                            setDialogOpen(true);
                        }
                    }}
                    editConfig={{
                        mode: "custom",
                        onEdit: handleEdit
                    }}
                    deleteConfig={{
                        confirm: true,
                        confirmTitle: "Delete Override",
                        confirmMessage: "Are you sure you want to delete this response override? Data once deleted can not be recovered.",
                        onDelete: handleDelete
                    }}
                    tableName="Response Message Overrides"
                    responsivePageSize={true}
                    responsivePageConfig={{heightRatio: 0.85}}
                />
            </Box>

            <RespMessageDialog
                open={dialogOpen}
                mode={dialogMode}
                initialValues={dialogInitial}
                onSave={handleDialogSave}
                onCancel={handleDialogCancel}
            />
            {snackbar}
        </Stack>
    );
};

export default RespMessages;